import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDoubleDownIcon,
  ChevronDoubleUpIcon,
  PlusIcon,
  SearchIcon,
  XIcon,
} from '@heroicons/react/outline';
import {
  giftItemBrandListDataAtom,
  giftItemBrandListStatusAtom,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import {
  deleteGiftItemBrand,
  reorderGiftItemBrands,
} from '@shared/api/giftItemBrand/giftItemBrand.controller';
import {
  giftItemBrandListUrl,
  useGiftItemBrandListHook,
} from '@shared/api/giftItemBrand/giftItemBrand.hook';
import { GiftItemBrand } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { GiftChoiceUnit } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import {
  giftItemInfoBrandFilterAtom,
  giftItemInfoDeliveryCategoryFilterAtom,
  giftItemInfoSearchQueryAtom,
  giftItemInfoStatusFilterAtom,
  giftItemInfoSubCategoryFilterAtom,
  giftItemInfoTaxFilterAtom,
  giftItemInfoTypeFilterAtom,
} from '@shared/api/giftItemInfo/giftItemInfo.atom';
import { adminGiftBrandSellerList } from '@shared/api/user/user.controller';
import { User } from '@shared/api/user/user.interface';
import { Loading } from '@shared/components/Loading';
import { api_ } from '@shared/plugIn/axios';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import axios from 'axios';
import { useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useAsyncFn } from 'react-use';

import styles from '../components/GiftBrandManagement.module.css';
import { BrandDeliveryCategory } from '../components/GiftBrandCategorySummary';
import { GiftBrandRow } from '../components/GiftBrandRow';
import { useSessionPersistedState } from '../hooks/useSessionPersistedState';
import { GiftItemInfoUsageLite, buildBrandItemMap } from '../utils/choiceUnitUsage.utils';

/**
 * 브랜드별 원상품 — 원상품 목록을 필요한 필드만 한 번 읽어 브랜드 기준 맵으로 뒤집는다 (개수 요약용).
 * 실패(라우트 미개방 등)하면 null — 이 정보만 숨기고 목록은 그대로 동작한다.
 */
const fetchBrandItemMap = async (
  role: LowerRole,
): Promise<Map<string, GiftItemInfoUsageLite[]> | null> => {
  try {
    const { data } = await api_.get<GiftItemInfoUsageLite[]>(
      `/${role}/gift-item-info?select=${encodeURIComponent(
        'name isEnabled giftItemBrand type giftDeliveryCategory',
      )}`,
    );
    return buildBrandItemMap(data);
  } catch {
    return null;
  }
};

const fetchDeliveryCategories = async (
  role: LowerRole,
): Promise<BrandDeliveryCategory[] | null> => {
  try {
    const { data } = await api_.get<BrandDeliveryCategory[]>(
      `/${role}/gift-delivery-category?select=${encodeURIComponent(
        'name isEnabled order',
      )}`,
    );
    return data;
  } catch {
    return null;
  }
};

/**
 * 브랜드별 선택상품(쿠폰형 선택지 유닛) — 원상품과 같은 요약으로 그리기 위해 모양을 맞춘다.
 * 실패하면 null — 선택상품 표기만 숨긴다.
 */
const fetchBrandChoiceUnitMap = async (
  role: LowerRole,
): Promise<Map<string, GiftItemInfoUsageLite[]> | null> => {
  try {
    const { data } = await api_.get<GiftChoiceUnit[]>(
      `/${role}/gift-choice-unit?select=${encodeURIComponent(
        'name enabled giftItemBrand',
      )}`,
    );
    return buildBrandItemMap(
      data.map((unit) => ({
        _id: unit._id,
        name: unit.name,
        isEnabled: unit.enabled,
        giftItemBrand: unit.giftItemBrand,
      })),
    );
  } catch {
    return null;
  }
};

/**
 * 브랜드별 배송관리자 계정 — admin 전용 API라 관리자일 때만 호출한다
 * (GiftAgent가 admin 제네릭을 부르면 401 → 강제 로그아웃). 실패하면 null.
 */
const fetchBrandSellerMap = async (): Promise<Map<string, User[]> | null> => {
  try {
    const users = await adminGiftBrandSellerList();
    const map = new Map<string, User[]>();
    users.forEach((user) => {
      const brands =
        user.giftItemBrands ?? (user.giftItemBrand ? [user.giftItemBrand] : []);
      brands.forEach((brand) => {
        const id = typeof brand === 'string' ? brand : brand?._id;
        if (!id) return;
        const list = map.get(id);
        if (list) list.push(user);
        else map.set(id, [user]);
      });
    });
    return map;
  } catch {
    return null;
  }
};

//* 선택상품 목록 페이지의 브랜드 필터 저장 키 (GiftChoiceUnitListPage의 useSessionPersistedState)
const CHOICE_UNIT_BRAND_FILTER_KEY = 'giftChoiceUnitList.brand';

// 배송형 여부 — 배송 상세나 감사인쇄가 설정된 브랜드
const isDeliveryBrand = (brand: GiftItemBrand) => {
  const d = brand.deliveryDetail;
  return Boolean(
    brand.thanksPrint?.enabled ||
      (d &&
        (d.shippingInfo ||
          d.shippingFeeInfo ||
          d.shippingFee != null ||
          d.freeShippingMinQuantity != null ||
          (d.notifyPhoneNumbers && d.notifyPhoneNumbers.length > 0))),
  );
};

// 쿠폰형 여부 — 쿠폰 상세가 설정된 브랜드
const isCouponBrand = (brand: GiftItemBrand) =>
  Boolean(brand.couponDetail?.validPeriod || brand.couponDetail?.placeOfExchange);

type BrandTypeFilter = 'all' | 'delivery' | 'coupon' | 'none';

// 둘 다 설정된 브랜드는 배송형·쿠폰형 필터 양쪽에 포함
const matchesTypeFilter = (brand: GiftItemBrand, filter: BrandTypeFilter) => {
  switch (filter) {
    case 'delivery':
      return isDeliveryBrand(brand);
    case 'coupon':
      return isCouponBrand(brand);
    case 'none':
      return !isDeliveryBrand(brand) && !isCouponBrand(brand);
    default:
      return true;
  }
};

export const GiftItemBrandListPage: FC = function GiftItemBrandListPage() {
  const { sellerRole, isAdmin } = useAuth();

  useGiftItemBrandListHook(sellerRole());

  // 정산정보는 플랫폼↔공급사 정산용이라 관리자에게만 노출
  const showSettlement = isAdmin();

  const status = useRecoilValue(giftItemBrandListStatusAtom);
  const data = useRecoilValue(giftItemBrandListDataAtom);
  const setData = useSetRecoilState(giftItemBrandListDataAtom);

  const [searchQuery, setSearchQuery] = useSessionPersistedState(
    'giftItemBrandList.search',
    '',
  );
  const [typeFilter, setTypeFilter] = useSessionPersistedState<BrandTypeFilter>(
    'giftItemBrandList.type',
    'all',
  );
  const [deletingId, setDeletingId] = useState<string | null>(null);

  //* 브랜드별 원상품 표기용 (실패해도 목록은 정상)
  //* deps는 반드시 role '값' — sellerRole 함수는 매 렌더 새 참조라 넣으면 무한 조회가 된다.
  const role = sellerRole();
  const [{ value: brandItemMap, loading: loadingBrandItems }, loadBrandItems] =
    useAsyncFn(() => fetchBrandItemMap(role), [role]);
  useEffect(() => {
    loadBrandItems();
  }, [loadBrandItems]);
  const [{ value: deliveryCategories, loading: loadingCategories }, loadCategories] =
    useAsyncFn(() => fetchDeliveryCategories(role), [role]);
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);
  const [{ value: brandChoiceUnitMap }, loadBrandChoiceUnits] = useAsyncFn(
    () => fetchBrandChoiceUnitMap(role),
    [role],
  );
  useEffect(() => {
    loadBrandChoiceUnits();
  }, [loadBrandChoiceUnits]);

  // 브랜드별 배송관리자 계정 (관리자만 — GiftAgent는 호출 자체를 안 함)
  const canSeeSellers = isAdmin();
  const [{ value: brandSellerMap }, loadBrandSellers] = useAsyncFn(
    () => (canSeeSellers ? fetchBrandSellerMap() : Promise.resolve(null)),
    [canSeeSellers],
  );
  useEffect(() => {
    loadBrandSellers();
  }, [loadBrandSellers]);

  // 원상품 목록으로 이동하며 브랜드 필터를 걸어 둔다
  const navigate = useNavigate();
  const setItemInfoBrandFilter = useSetRecoilState(giftItemInfoBrandFilterAtom);
  const setItemInfoCategoryFilter = useSetRecoilState(
    giftItemInfoDeliveryCategoryFilterAtom,
  );
  const setItemInfoTypeFilter = useSetRecoilState(giftItemInfoTypeFilterAtom);
  const setItemInfoSearch = useSetRecoilState(giftItemInfoSearchQueryAtom);
  const setItemInfoStatus = useSetRecoilState(giftItemInfoStatusFilterAtom);
  const setItemInfoSubCategory = useSetRecoilState(giftItemInfoSubCategoryFilterAtom);
  const setItemInfoTax = useSetRecoilState(giftItemInfoTaxFilterAtom);
  // 연결 변경은 원상품에서 한다. 이전 검색 조건이 대상 상품을 가리지 않도록 초기화한다.
  const openItemInfoList = (brandId: string, categoryId?: string) => {
    setItemInfoBrandFilter(brandId);
    setItemInfoCategoryFilter(categoryId ?? 'all');
    setItemInfoTypeFilter(categoryId === undefined ? 'all' : 'Delivery');
    setItemInfoSearch('');
    setItemInfoStatus('all');
    setItemInfoSubCategory('all');
    setItemInfoTax('all');
    navigate('/gift-seller/item-info');
  };
  // 배송관리자 계정 탭으로 이동하며 브랜드 필터(?brand=)를 걸어 둔다
  const openBrandSellerList = useCallback(
    (brandId: string) => navigate(`/gift-seller/brand?tab=seller&brand=${brandId}`),
    [navigate],
  );

  // 선택상품 목록으로 이동하며 브랜드 필터를 걸어 둔다 (그 페이지는 sessionStorage에서 복원)
  const openChoiceUnitList = useCallback(
    (brandId: string) => {
      try {
        sessionStorage.setItem(CHOICE_UNIT_BRAND_FILTER_KEY, JSON.stringify(brandId));
      } catch {
        // 저장 실패 시 필터 없이 이동
      }
      navigate('/gift-seller/choice-unit');
    },
    [navigate],
  );

  // 순서 편집 모드 — null이면 일반 목록, 배열이면 편집 중인 순서(브랜드 id)
  const queryClient = useQueryClient();
  const [reorderIds, setReorderIds] = useState<string[] | null>(null);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  // 유형별 개수 (필터 칩 표시용)
  const typeCounts = useMemo(() => {
    const list = data ?? [];
    return {
      all: list.length,
      delivery: list.filter(isDeliveryBrand).length,
      coupon: list.filter(isCouponBrand).length,
      none: list.filter((b) => matchesTypeFilter(b, 'none')).length,
    };
  }, [data]);

  // 유형·검색 필터 — 순서는 서버 정렬(표시 순서 → 이름) 그대로
  const filteredData = useMemo(() => {
    if (!data) return [];

    const query = searchQuery.toLowerCase().trim();
    return data.filter(
      (item) =>
        matchesTypeFilter(item, typeFilter) &&
        (!query || item.name.toLowerCase().includes(query)),
    );
  }, [data, searchQuery, typeFilter]);

  // 순서 편집: 필터·검색 무시하고 전체 브랜드를 편집 순서대로
  const reorderList = useMemo(() => {
    if (!reorderIds || !data) return [];
    const byId = new Map(data.map((b) => [b._id, b]));
    return reorderIds
      .map((id) => byId.get(id))
      .filter((b): b is GiftItemBrand => Boolean(b));
  }, [reorderIds, data]);

  const startReorder = () => setReorderIds((data ?? []).map((b) => b._id));
  const cancelReorder = () => setReorderIds(null);

  // from 위치의 브랜드를 to 위치로 옮김
  const moveBrand = (from: number, to: number) => {
    setReorderIds((prev) => {
      if (!prev || to < 0 || to >= prev.length || from === to) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      if (moved === undefined) return prev;
      next.splice(to, 0, moved);
      return next;
    });
  };

  const saveReorder = async () => {
    if (!reorderIds) return;
    try {
      setIsSavingOrder(true);
      await reorderGiftItemBrands(reorderIds);
      // 목록 원자를 즉시 새 순서로 맞추고, 캐시도 무효화해 다른 화면 드롭다운까지 반영
      const orderIndex = new Map(reorderIds.map((id, i) => [id, i]));
      setData((prev) =>
        [...prev]
          .map((b) =>
            orderIndex.has(b._id) ? { ...b, order: orderIndex.get(b._id) } : b,
          )
          .sort(
            (a, b) =>
              (orderIndex.get(a._id) ?? Number.MAX_SAFE_INTEGER) -
              (orderIndex.get(b._id) ?? Number.MAX_SAFE_INTEGER),
          ),
      );
      await queryClient.invalidateQueries(giftItemBrandListUrl(sellerRole()));
      setReorderIds(null);
      alert('순서가 저장되었습니다.');
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        alert('서버에 순서 저장 기능이 아직 배포되지 않았습니다.');
      } else {
        alert('순서 저장에 실패했습니다.');
      }
    } finally {
      setIsSavingOrder(false);
    }
  };

  const isFiltered = Boolean(searchQuery) || typeFilter !== 'all';

  // 등록/수정 폼에 유형 힌트 전달 — 반대 유형 섹션을 접어서 연다
  const typeQuery =
    typeFilter === 'delivery' || typeFilter === 'coupon' ? `?type=${typeFilter}` : '';

  // 삭제 핸들러
  const handleDelete = useCallback(
    async (brand: GiftItemBrand) => {
      if (!confirm(`"${brand.name}" 브랜드를 삭제하시겠습니까?`)) return;

      try {
        setDeletingId(brand._id);
        await deleteGiftItemBrand(brand._id, sellerRole());
        // 목록에서 제거
        setData((prev) => prev.filter((item) => item._id !== brand._id));
        alert('삭제되었습니다.');
      } catch (error) {
        console.error(error);
        alert('삭제에 실패했습니다.');
      } finally {
        setDeletingId(null);
      }
    },
    [sellerRole, setData],
  );

  const resetFilters = () => {
    setSearchQuery('');
    setTypeFilter('all');
  };
  const orderChanged = Boolean(
    reorderIds?.some((id, index) => id !== data?.[index]?._id),
  );
  const secondaryButton =
    'inline-flex items-center justify-center gap-2 rounded border border-gray-300 bg-white px-3.5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40';
  const primaryButton =
    'inline-flex items-center justify-center gap-2 rounded bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div
      className={`${styles.page} min-h-screen bg-gray-50 px-4 py-6 text-gray-900 sm:px-6 lg:px-8 lg:py-8`}
    >
      <div className="mx-auto max-w-[1440px]">
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">상품 브랜드</h1>
            <p className="mt-2 text-sm text-gray-500">
              브랜드별 상품과 배송관리자, 운영 설정을 관리합니다.
            </p>
          </div>
          {reorderIds ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={cancelReorder}
                disabled={isSavingOrder}
                className={secondaryButton}
              >
                취소
              </button>
              <button
                type="button"
                onClick={saveReorder}
                disabled={isSavingOrder || !orderChanged}
                className={primaryButton}
              >
                {isSavingOrder ? '저장 중…' : '순서 저장'}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {isAdmin() && status === 'success' && (data?.length ?? 0) > 1 && (
                <button type="button" onClick={startReorder} className={secondaryButton}>
                  순서 변경
                </button>
              )}
              <Link
                to={`/gift-seller/item-brand/new${typeQuery}`}
                className={primaryButton}
              >
                <PlusIcon aria-hidden className="h-4 w-4" />
                브랜드 등록
              </Link>
            </div>
          )}
        </header>

        {reorderIds ? (
          <section
            className="rounded-md border border-gray-200 bg-white"
            aria-label="표시 순서 편집"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-5 py-4">
              <div>
                <h2 className="text-sm font-semibold">표시 순서 편집</h2>
                <p className="mt-1 text-sm text-gray-500">
                  전체 브랜드의 순서를 변경합니다. 이동 후 순서 저장을 눌러주세요.
                </p>
              </div>
              <span role="status" className="text-xs text-gray-500">
                {orderChanged ? '저장하지 않은 변경사항' : `전체 ${reorderList.length}개`}
              </span>
            </div>
            <ul aria-label="브랜드 표시 순서">
              {reorderList.map((item, index) => (
                <li
                  key={item._id}
                  className="flex items-center gap-3 border-b border-gray-100 px-4 py-4 last:border-b-0 sm:px-5"
                >
                  <span className="w-7 shrink-0 text-sm tabular-nums text-gray-400">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="min-w-0 flex-1 break-words text-sm font-medium">
                    {item.name}
                  </span>
                  <span className="hidden text-xs text-gray-500 sm:block">
                    {[isDeliveryBrand(item) && '배송형', isCouponBrand(item) && '쿠폰형']
                      .filter(Boolean)
                      .join(' · ') || '미지정'}
                  </span>
                  <div className="flex shrink-0 gap-1">
                    {[
                      {
                        label: '맨 위로 이동',
                        to: 0,
                        icon: ChevronDoubleUpIcon,
                        disabled: index === 0,
                      },
                      {
                        label: '위로 이동',
                        to: index - 1,
                        icon: ArrowUpIcon,
                        disabled: index === 0,
                      },
                      {
                        label: '아래로 이동',
                        to: index + 1,
                        icon: ArrowDownIcon,
                        disabled: index === reorderList.length - 1,
                      },
                      {
                        label: '맨 아래로 이동',
                        to: reorderList.length - 1,
                        icon: ChevronDoubleDownIcon,
                        disabled: index === reorderList.length - 1,
                      },
                    ].map(({ label, to, icon: Icon, disabled }) => (
                      <button
                        key={label}
                        type="button"
                        aria-label={`${item.name} ${label}`}
                        title={label}
                        onClick={() => moveBrand(index, to)}
                        disabled={disabled || isSavingOrder}
                        className="rounded border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 disabled:opacity-25"
                      >
                        <Icon aria-hidden className="h-4 w-4" />
                      </button>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ) : (
          <section
            className="rounded-md border border-gray-200 bg-white"
            aria-label="브랜드 목록"
          >
            <div className="flex flex-col gap-4 px-5 pt-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-1" aria-label="브랜드 유형 필터">
                {(
                  [
                    { key: 'all', label: '전체' },
                    { key: 'delivery', label: '배송형' },
                    { key: 'coupon', label: '쿠폰형' },
                    { key: 'none', label: '미지정' },
                  ] as { key: BrandTypeFilter; label: string }[]
                )
                  .filter(
                    (filter) =>
                      filter.key !== 'none' ||
                      typeCounts.none > 0 ||
                      typeFilter === 'none',
                  )
                  .map((filter) => (
                    <button
                      key={filter.key}
                      type="button"
                      aria-pressed={typeFilter === filter.key}
                      onClick={() => setTypeFilter(filter.key)}
                      className={`flex items-center gap-2 rounded px-3 py-2 text-sm ${
                        typeFilter === filter.key
                          ? 'bg-gray-900 font-medium text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {filter.label}
                      <span
                        className={`text-xs tabular-nums ${
                          typeFilter === filter.key ? 'text-gray-300' : 'text-gray-400'
                        }`}
                      >
                        {status === 'success' ? typeCounts[filter.key] : '–'}
                      </span>
                    </button>
                  ))}
              </div>
              <div className="relative w-full lg:w-72">
                <SearchIcon
                  aria-hidden
                  className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-gray-400"
                />
                <input
                  type="search"
                  aria-label="브랜드명 검색"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="브랜드명 검색"
                  className="h-10 w-full rounded border border-gray-300 bg-white pl-9 pr-10 text-sm outline-none placeholder:text-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 [&::-webkit-search-cancel-button]:appearance-none"
                />
                {searchQuery && (
                  <button
                    type="button"
                    aria-label="검색어 지우기"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-1 top-1 rounded p-2 text-gray-400 hover:text-gray-700"
                  >
                    <XIcon aria-hidden className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex min-h-[52px] flex-wrap items-center justify-between gap-2 px-5 py-3">
              <p role="status" className="text-xs text-gray-500">
                {status === 'success' ? (
                  <>
                    총{' '}
                    <strong className="font-semibold tabular-nums text-gray-900">
                      {filteredData.length}
                    </strong>
                    개{isFiltered && ` / 전체 ${data?.length ?? 0}개`}
                  </>
                ) : status === 'error' ? (
                  '조회 실패'
                ) : (
                  '불러오는 중'
                )}
              </p>
              {isFiltered && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-xs text-gray-600 underline underline-offset-4 hover:text-gray-900"
                >
                  필터 초기화
                </button>
              )}
            </div>
            {status === 'error' ? (
              <div
                role="alert"
                className="border-t border-gray-200 px-5 py-16 text-center"
              >
                <p className="font-medium">브랜드 목록을 불러오지 못했습니다.</p>
                <p className="mt-2 text-sm text-gray-500">잠시 후 다시 시도해주세요.</p>
                <button
                  type="button"
                  onClick={() =>
                    queryClient.invalidateQueries(giftItemBrandListUrl(role))
                  }
                  className={`${secondaryButton} mt-5`}
                >
                  다시 시도
                </button>
              </div>
            ) : status !== 'success' ? (
              <div
                className="flex justify-center border-t border-gray-200 py-20"
                aria-label="브랜드 목록 불러오는 중"
              >
                <Loading />
              </div>
            ) : filteredData.length === 0 ? (
              <div className="border-t border-gray-200 px-5 py-20 text-center">
                <h2 className="font-medium">
                  {isFiltered
                    ? '검색 조건에 맞는 브랜드가 없습니다'
                    : '첫 브랜드를 등록해보세요'}
                </h2>
                <p className="mt-2 text-sm text-gray-500">
                  {isFiltered
                    ? '검색어나 유형 필터를 변경해주세요.'
                    : '브랜드를 등록한 다음 상품과 배송관리자를 연결할 수 있습니다.'}
                </p>
                {!isFiltered && (
                  <Link
                    to="/gift-seller/item-brand/new"
                    className={`${primaryButton} mt-5`}
                  >
                    <PlusIcon aria-hidden className="h-4 w-4" />
                    브랜드 등록
                  </Link>
                )}
              </div>
            ) : (
              <table className="block w-full table-fixed text-left text-sm xl:table">
                <caption className="sr-only">
                  브랜드별 상품, 배송관리자 및 운영 정보
                </caption>
                <thead className="hidden border-t border-gray-200 bg-gray-50 xl:table-header-group">
                  <tr>
                    {[
                      '브랜드 / 유형',
                      '상품 현황',
                      ...(showSettlement ? ['배송관리자'] : []),
                      '운영 정보',
                      '관리',
                    ].map((heading, index) => (
                      <th
                        key={heading}
                        scope="col"
                        className={`px-5 py-3 text-xs font-medium text-gray-500 ${
                          index === 0 ? 'w-1/4' : ''
                        } ${heading === '관리' ? 'w-52 text-right' : ''}`}
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="block xl:table-row-group">
                  {filteredData.map((item) => (
                    <GiftBrandRow
                      key={item._id}
                      brand={item}
                      delivery={isDeliveryBrand(item)}
                      coupon={isCouponBrand(item)}
                      showSettlement={showSettlement}
                      typeQuery={typeQuery}
                      products={
                        brandItemMap ? brandItemMap.get(item._id) ?? [] : brandItemMap
                      }
                      deliveryCategories={deliveryCategories}
                      retryingCategories={loadingBrandItems || loadingCategories}
                      onRetryCategories={() => {
                        void loadBrandItems();
                        void loadCategories();
                      }}
                      onCategoryProducts={(categoryId) =>
                        openItemInfoList(item._id, categoryId ?? 'all')
                      }
                      choices={
                        brandChoiceUnitMap
                          ? brandChoiceUnitMap.get(item._id) ?? []
                          : brandChoiceUnitMap
                      }
                      sellers={
                        brandSellerMap
                          ? brandSellerMap.get(item._id) ?? []
                          : brandSellerMap
                      }
                      deleting={deletingId === item._id}
                      onProducts={() => openItemInfoList(item._id)}
                      onChoices={() => openChoiceUnitList(item._id)}
                      onSellers={() => openBrandSellerList(item._id)}
                      onDelete={() => handleDelete(item)}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </section>
        )}
      </div>
    </div>
  );
};
